import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import DefaultWebLayout from './layouts/_default-web-layout.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import { ROLES } from './config/roles.js';
import './App.scss';

// Feature pages are generated per use case (ui-components-from-reference) and
// wired here, routed by slug under the default layout. Status pages and the home
// placeholder are lazy-loaded; the catch-all renders the themed 404.
const RoleLanding = lazy(() => import('./components/RoleLanding.jsx'));
const FinanceOverview = lazy(() => import('./pages/overview/FinanceOverview.jsx'));
const WarehouseOverview = lazy(() => import('./pages/overview/WarehouseOverview.jsx'));
const RegionalOverview = lazy(() => import('./pages/overview/RegionalOverview.jsx'));
const Account = lazy(() => import('./pages/account/Account.jsx'));
const Login = lazy(() => import('./pages/auth/Login.jsx'));
const Register = lazy(() => import('./pages/auth/Register.jsx'));
const LocationDesigner = lazy(() => import('./pages/warehouse/LocationDesigner.jsx'));
const InboundReceiving = lazy(() => import('./pages/receiving/InboundReceiving.jsx'));
const DirectedPutaway = lazy(() => import('./pages/movement/DirectedPutaway.jsx'));
const StorageOccupancyMonitor = lazy(() => import('./pages/occupancy/StorageOccupancyMonitor.jsx'));
const ReplenishmentPlanner = lazy(() => import('./pages/replenishment/ReplenishmentPlanner.jsx'));
const CycleCountConsole = lazy(() => import('./pages/cyclecount/CycleCountConsole.jsx'));
const BinTransferTerminal = lazy(() => import('./pages/inventory/BinTransferTerminal.jsx'));
const ChannelIntegrationCatalog = lazy(() => import('./pages/channels/ChannelIntegrationCatalog.jsx'));
const OAuthAuthorize = lazy(() => import('./pages/channels/OAuthAuthorize.jsx'));
const OAuthCallback = lazy(() => import('./pages/channels/OAuthCallback.jsx'));
const CourierSettings = lazy(() => import('./pages/courier/CourierSettings.jsx'));
const OrderRoutingRules = lazy(() => import('./pages/routing/OrderRoutingRules.jsx'));
const WavePickingConsole = lazy(() => import('./pages/picking/WavePickingConsole.jsx'));
const PickQueueConsole = lazy(() => import('./pages/pickqueue/PickQueueConsole.jsx'));
const PackingStation = lazy(() => import('./pages/packing/PackingStation.jsx'));
const OrderFulfilmentQueue = lazy(() => import('./pages/fulfilment/OrderFulfilmentQueue.jsx'));
const ReturnsTerminal = lazy(() => import('./pages/returns/ReturnsTerminal.jsx'));
const CountryProfiles = lazy(() => import('./pages/country/CountryProfiles.jsx'));
const TaxCurrencyManager = lazy(() => import('./pages/taxcurrency/TaxCurrencyManager.jsx'));
const BillingRuleConfigurator = lazy(() => import('./pages/billing/BillingRuleConfigurator.jsx'));
const RemittanceUploader = lazy(() => import('./pages/remittance/RemittanceUploader.jsx'));
const DiscrepancyWorklist = lazy(() => import('./pages/discrepancy/DiscrepancyWorklist.jsx'));
const BillingInvoicesGate = lazy(() => import('./pages/invoice/BillingInvoicesGate.jsx'));
const RtsClaimsLedger = lazy(() => import('./pages/rts/RtsClaimsLedger.jsx'));
const MerchantOrdersDashboard = lazy(() => import('./pages/merchantorders/MerchantOrdersDashboard.jsx'));
const MerchantCodCollections = lazy(() => import('./pages/cod/MerchantCodCollections.jsx'));
const MerchantPortalDashboard = lazy(() => import('./pages/dashboard/MerchantPortalDashboard.jsx'));
const RegionalPerformance = lazy(() => import('./pages/regional/RegionalPerformance.jsx'));
const SyncMonitor = lazy(() => import('./pages/sync/SyncMonitor.jsx'));
const CrossBorderDashboard = lazy(() => import('./pages/crossborder/CrossBorderDashboard.jsx'));
const NotFound = lazy(() => import('./pages/status/NotFound.jsx'));
const Unauthorized = lazy(() => import('./pages/status/Unauthorized.jsx'));
const Forbidden = lazy(() => import('./pages/status/Forbidden.jsx'));
const ServerError = lazy(() => import('./pages/status/ServerError.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Anonymous auth screens — standalone, no app chrome. */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Merchant OAuth flow — standalone, authenticated Merchant User. */}
            <Route element={<RoleRoute allow={[ROLES.MERCHANT_USER]} />}>
              <Route path="/oauth/authorize/:connector" element={<OAuthAuthorize />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
            </Route>

            {/* All web pages render inside the shared default layout. */}
            <Route element={<DefaultWebLayout />}>
              <Route index element={<RoleLanding />} />

              {/* Generic status pages */}
              <Route path="401" element={<Unauthorized />} />
              <Route path="403" element={<Forbidden />} />
              <Route path="500" element={<ServerError />} />

              {/* Account / profile — any authenticated user. */}
              <Route element={<RoleRoute />}>
                <Route path="account" element={<Account />} />
              </Route>

              {/* Feature routes (by slug) are added here as pages are generated. */}
              {/* Configure Warehouse Layout / Monitor Occupancy — Operator Admin only. */}
              <Route element={<RoleRoute allow={[ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="warehouse-location-designer-bin-configurator"
                  element={<LocationDesigner />}
                />
                <Route
                  path="storage-location-occupancy-monitor"
                  element={<StorageOccupancyMonitor />}
                />
              </Route>

              {/* Channel Integrations — Operator Admin (credentials) + Merchant User (OAuth connect). */}
              <Route element={<RoleRoute allow={[ROLES.OPERATOR_ADMIN, ROLES.MERCHANT_USER]} />}>
                <Route
                  path="channel-integration-catalog"
                  element={<ChannelIntegrationCatalog />}
                />
              </Route>

              {/* Cross-Border Logistics Dashboard — Cross-Border Ops + Operator Admin. */}
              <Route element={<RoleRoute allow={[ROLES.CROSS_BORDER_OPS, ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="cross-border-logistics-dashboard"
                  element={<CrossBorderDashboard />}
                />
              </Route>

              {/* Merchant Orders Dashboard / COD Collections — Merchant User (own tenant) + Operator Admin. */}
              <Route element={<RoleRoute allow={[ROLES.MERCHANT_USER, ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="merchant-orders-dashboard"
                  element={<MerchantOrdersDashboard />}
                />
                <Route
                  path="merchant-cod-collections"
                  element={<MerchantCodCollections />}
                />
                <Route
                  path="client-analytics-dashboard"
                  element={<MerchantPortalDashboard />}
                />
                <Route
                  path="regional-merchant-performance"
                  element={<RegionalPerformance />}
                />
              </Route>

              {/* Manage Courier Configurations / Order Routing Rules / Integration Health — Operator Admin only. */}
              <Route element={<RoleRoute allow={[ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="courier-settings-service-zones"
                  element={<CourierSettings />}
                />
                <Route
                  path="order-routing-rules-setup"
                  element={<OrderRoutingRules />}
                />
                <Route
                  path="integration-sync-api-monitor"
                  element={<SyncMonitor />}
                />
              </Route>

              {/* Configure Country Operational Profile — Regional Admin + Operator Admin. */}
              <Route element={<RoleRoute allow={[ROLES.REGIONAL_ADMIN, ROLES.OPERATOR_ADMIN]} />}>
                <Route path="regional-overview" element={<RegionalOverview />} />
                <Route
                  path="country-operations-profile"
                  element={<CountryProfiles />}
                />
              </Route>

              {/* Multi-Currency & Regional Tax / Billing Rules — Operator Admin + Finance User. */}
              <Route element={<RoleRoute allow={[ROLES.OPERATOR_ADMIN, ROLES.FINANCE_USER]} />}>
                <Route path="finance-overview" element={<FinanceOverview />} />
                <Route
                  path="regional-tax-and-currency-manager"
                  element={<TaxCurrencyManager />}
                />
                <Route
                  path="client-billing-rule-configurator"
                  element={<BillingRuleConfigurator />}
                />
                <Route
                  path="cod-remittance-uploader"
                  element={<RemittanceUploader />}
                />
                <Route
                  path="cod-discrepancy-reconciliation-worklist"
                  element={<DiscrepancyWorklist />}
                />
                <Route
                  path="billing-e-invoices-gate"
                  element={<BillingInvoicesGate />}
                />
                <Route
                  path="courier-rts-transit-damage-claims-ledger"
                  element={<RtsClaimsLedger />}
                />
              </Route>

              {/* Inbound Receiving & QA — Warehouse Operator only. */}
              <Route element={<RoleRoute allow={[ROLES.WAREHOUSE_OPERATOR]} />}>
                <Route
                  path="inbound-po-receiving-qa-terminal"
                  element={<InboundReceiving />}
                />
              </Route>

              {/* Process Order Fulfilment — Warehouse Operator + Operator Admin. */}
              <Route element={<RoleRoute allow={[ROLES.WAREHOUSE_OPERATOR, ROLES.OPERATOR_ADMIN]} />}>
                <Route path="warehouse-overview" element={<WarehouseOverview />} />
                <Route
                  path="warehouse-orders-queue"
                  element={<OrderFulfilmentQueue />}
                />
              </Route>

              {/* Execute Directed Putaway — Warehouse Operator only. */}
              <Route element={<RoleRoute allow={[ROLES.WAREHOUSE_OPERATOR]} />}>
                <Route
                  path="directed-putaway-stock-movement-monitor"
                  element={<DirectedPutaway />}
                />
              </Route>

              {/* Wave & Batch Picking / Pick Queue — Warehouse Operator + Operator Admin. */}
              <Route element={<RoleRoute allow={[ROLES.WAREHOUSE_OPERATOR, ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="wave-batch-picking-console"
                  element={<WavePickingConsole />}
                />
                <Route
                  path="pick-queue-execution"
                  element={<PickQueueConsole />}
                />
                <Route
                  path="packing-station-terminal"
                  element={<PackingStation />}
                />
                <Route
                  path="returns-intake-inspection-terminal"
                  element={<ReturnsTerminal />}
                />
              </Route>

              {/* Replenishment & Min-Max Planner — Operator Admin (config) + Warehouse Operator (execute). */}
              <Route element={<RoleRoute allow={[ROLES.OPERATOR_ADMIN, ROLES.WAREHOUSE_OPERATOR]} />}>
                <Route
                  path="replenishment-min-max-planner"
                  element={<ReplenishmentPlanner />}
                />
              </Route>

              {/* Cycle Count & Inventory Adjustment — Operator counts, Finance/Manager approves. */}
              <Route element={<RoleRoute allow={[ROLES.WAREHOUSE_OPERATOR, ROLES.FINANCE_USER, ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="cycle-count-inventory-adjustment-console"
                  element={<CycleCountConsole />}
                />
              </Route>

              {/* Bin Transfer & Adjustment — Operator transfers/adjusts, Admin approves over-threshold. */}
              <Route element={<RoleRoute allow={[ROLES.WAREHOUSE_OPERATOR, ROLES.OPERATOR_ADMIN]} />}>
                <Route
                  path="bin-transfer-adjustment-terminal"
                  element={<BinTransferTerminal />}
                />
              </Route>

              {/* Catch-all → themed 404 (the fallback for unbuilt slug routes). */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
